import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Card, Timeline, Tag, Spin, Result, Button, Statistic, Divider, Input, Space } from 'antd';
import { ArrowRightOutlined, SearchOutlined, DollarOutlined, BankOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import ClientSelect from '../../components/common/ClientSelect';
import { clientsApi, extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali } from '../../utils/dateUtils';

const { Title, Text } = Typography;

const ClientFinancialProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchId, setSearchId] = useState(id || '');

  useEffect(() => {
    // بدون id: اولین مشتری را انتخاب کن (فقط یک رکورد، نه کل لیست)
    if (id) return;
    const pickFirstClient = async () => {
      try {
        const res = await clientsApi.getOptions({ per_page: 1, include_inactive: true });
        const first = (res?.data || [])[0];
        if (first) {
          setSearchId(String(first.id));
          fetchProfile(first.id);
        }
      } catch (err) {
        console.error('Failed to load clients', err);
      }
    };
    pickFirstClient();
  }, []);

  const fetchProfile = async (clientId) => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await financialApi.getClientFinancialProfile(clientId);
      const data = res?.data?.financial_profile || res?.financial_profile;
      
      if (data) {
        setProfile(data);
      } else {
        setError('پروفایل مالی یافت نشد');
      }
    } catch (err) {
      console.error('Failed to fetch client profile:', err);
      setError(extractApiUserMessage(err) || 'خطا در دریافت پروفایل مالی مشتری');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProfile(id);
    }
  }, [id]);

  const handleSearch = () => {
    if (searchId) {
      navigate(`/finance/client-profile/${searchId}`);
    }
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button type="text" icon={<ArrowRightOutlined />} onClick={() => navigate(-1)} />
            <div>
              <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
                پروفایل مالی مشتری ۳۶۰
              </Title>
              <Text className="text-gray-500 dark:text-gray-400">
                گزارش جامع وضعیت مالی و تاریخچه تراکنش‌های مشتری
              </Text>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <ClientSelect
              value={searchId ? Number(searchId) : undefined}
              onChange={(val) => {
                setSearchId(String(val));
                navigate(`/finance/client-profile/${val}`);
                fetchProfile(val);
              }}
              style={{ width: 220 }}
              placeholder="انتخاب یا جستجوی مشتری"
              includeInactive
              allowClear={false}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-12">
            <Spin size="large" />
          </div>
        ) : error ? (
          <Result
            status="error"
            title="خطا"
            subTitle={error}
          />
        ) : !profile ? (
          <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl text-center p-12">
            <Text type="secondary">لطفاً یک شناسه مشتری برای مشاهده پروفایل وارد کنید.</Text>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={12}>
                  <Title level={4} className="m-0">{profile.client_name}</Title>
                  <Space className="mt-2">
                    <Tag color="blue">شناسه: {profile.client_id}</Tag>
                    {profile.credit_hold ? (
                      <Tag color="error">مسدود اعتباری</Tag>
                    ) : (
                      <Tag color="success">اعتبار فعال</Tag>
                    )}
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <div className="flex gap-8 md:justify-end">
                    <Statistic 
                      title="سقف اعتبار (تومان)" 
                      value={profile.credit_limit} 
                      formatter={(val) => formatPriceToman(val)}
                    />
                    <Statistic 
                      title="اعتبار در دسترس (تومان)" 
                      value={profile.available_credit} 
                      formatter={(val) => formatPriceToman(val)}
                      valueStyle={{ color: profile.available_credit < 0 ? '#cf1322' : '#3f8600' }}
                    />
                  </div>
                </Col>
              </Row>
            </Card>

            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="ارزش کل قراردادها" 
                        value={profile.total_deals_value} 
                        formatter={(val) => formatPriceToman(val)}
                        prefix={<DollarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="مانده بدهی (Outstanding)" 
                        value={profile.outstanding_balance} 
                        formatter={(val) => formatPriceToman(val)}
                        valueStyle={{ color: '#cf1322' }}
                        prefix={<WarningOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="کل پرداخت‌های تایید شده" 
                        value={profile.total_approved_payments} 
                        formatter={(val) => formatPriceToman(val)}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="خالص پرداختی (پس از عودت)" 
                        value={profile.net_paid} 
                        formatter={(val) => formatPriceToman(val)}
                      />
                      {profile.total_refunds > 0 && (
                        <Text type="secondary" className="text-xs mt-1 block">
                          شامل {formatPriceToman(profile.total_refunds)} عودت وجه
                        </Text>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="ارزش چک‌های نزد صندوق" 
                        value={profile.in_vault_cheques_amount} 
                        formatter={(val) => formatPriceToman(val)}
                        prefix={<BankOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl">
                      <Statistic 
                        title="چک‌های برگشتی" 
                        value={profile.bounced_cheques_amount} 
                        formatter={(val) => formatPriceToman(val)}
                        valueStyle={{ color: profile.bounced_cheques_amount > 0 ? '#cf1322' : 'inherit' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </Col>
              
              <Col xs={24} lg={8}>
                <Card bordered={false} className="shadow-sm dark:bg-gray-800 rounded-2xl h-full" title="تایم‌لاین رویدادهای مالی">
                  {profile.timeline && profile.timeline.length > 0 ? (
                    <Timeline
                      items={profile.timeline.map((item, index) => {
                        let color = 'blue';
                        if (item.type === 'payment_received') color = 'green';
                        if (item.type === 'cheque_bounced') color = 'red';
                        if (item.type === 'refund') color = 'orange';

                        return {
                          key: index,
                          color,
                          children: (
                            <div className="mb-4">
                              <div className="font-semibold">{item.title}</div>
                              <div className="text-gray-500 text-sm">{toJalali(item.date, 'jYYYY/jMM/jDD HH:mm') || toJalali(item.date) || '-'}</div>
                              {item.amount > 0 && (
                                <div className="text-gray-700 dark:text-gray-300 font-medium mt-1">
                                  {formatPriceToman(item.amount)} تومان
                                </div>
                              )}
                            </div>
                          )
                        };
                      })}
                    />
                  ) : (
                    <Text type="secondary">هیچ رویدادی یافت نشد</Text>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ClientFinancialProfile;
